import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {SliderModule} from 'primeng/slider';
import {UIChart} from 'primeng/chart';
import {Loading} from '@components/ui/loading/loading';
import {OldMapComponent} from '@components/old-map/old-map.component';
import {ServerTranslatePipe} from '@app/utils/pipes/server-translate-pipe';
import {IndexLayerOption} from '@shared/data/index-layer-options-data';
import {isCropPhaseActive} from '@app/pages/electronic-field-map/pages/fields/passport/forms/crop-chart-index/managers/crop-vegetation-phase.util';
import type {OldMapViewport, SnapshotTileState} from '@components/old-map/managers/map.manager';

@Component({
  selector: 'app-field-details-indices-map-chart-row',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SliderModule,
    UIChart,
    Loading,
    OldMapComponent,
    ServerTranslatePipe,
  ],
  templateUrl: './field-details-indices-map-chart-row.component.html',
  styleUrls: ['./field-details-indices-map-chart-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldDetailsIndicesMapChartRowComponent implements OnChanges {
  private readonly translate = inject(TranslateService);
  readonly snapshotUnavailableDismissed = signal(false);

  @Input({ required: true }) indexOptions: IndexLayerOption[] = [];
  @Input({ required: true }) selectedIndexValue = 'Ndvi';
  @Input() selectedTimelineDate: Date | null = null;

  @Input() indexTileUrl: string | null = null;
  @Input() snapshotTileUrl: string | null = null;
  @Input() snapshotRefreshSeq = 0;
  @Input() snapshotRestoreViewport: OldMapViewport | null = null;
  @Input() snapshotLoading = false;
  @Input() snapshotUnavailable = false;
  @Input() wmsTileLoading = false;
  @Input() wmsOverlayOpacity = 0.75;

  @Input({ required: true }) mapCenter: [number, number] = [0, 0];
  @Input() mapSeasonPolygonJson: string | null = null;
  @Input() fieldPolygonJson: string | null = null;
  @Input() mapPolygonColorOnMiniMap = '';

  @Input({ required: true }) mapIndexScaleBarGradient = '';

  @Input() chartLoading = false;
  @Input() chartData: unknown = null;
  @Input() chartOptions: unknown = null;

  @Input() cropPhases: any[] = [];
  @Input() plantingBoundaryDate: Date | null = null;

  @Output() indexPick = new EventEmitter<string>();
  @Output() wmsOverlayOpacityChange = new EventEmitter<number>();
  @Output() snapshotTileStateChange = new EventEmitter<SnapshotTileState>();
  @Output() snapshotViewportChange = new EventEmitter<OldMapViewport>();

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['selectedTimelineDate']
      || (changes['snapshotUnavailable']?.currentValue && !changes['snapshotUnavailable']?.previousValue)
    ) {
      this.snapshotUnavailableDismissed.set(false);
    }
  }

  dismissSnapshotUnavailable(): void {
    this.snapshotUnavailableDismissed.set(true);
  }

  indexLabel(opt: IndexLayerOption): string {
    const lang = this.translate.currentLang || 'en-US';
    return (opt.names as any)?.[lang] || (opt.names as any)?.['en-US'] || String(opt.value);
  }

  onIndexClick(value: string): void {
    this.indexPick.emit(value);
  }

  onWmsOpacityChange(v: number): void {
    this.wmsOverlayOpacityChange.emit(v);
  }

  onMapMove(event: {center?: [number, number]; zoom?: number}): void {
    if (event.center && event.zoom != null) {
      this.snapshotViewportChange.emit({center: event.center, zoom: event.zoom});
    }
  }

  phaseStyle(phaseIndex: number): Record<string, string> {
    const plant = this.plantingBoundaryDate;
    if (!plant || !this.cropPhases.length) {
      return {flex: '1'};
    }
    const ref = this.selectedTimelineDate ?? new Date();
    const active = isCropPhaseActive(this.cropPhases, phaseIndex, plant, ref);
    return {
      flex: '1',
      opacity: active ? '1' : '0.45',
      borderBottom: active ? '3px solid #22c55e' : '3px solid transparent',
    };
  }
}
